import { useMemo, useState } from "react";

import {
  analyzeTariffFile,
  createEmptyTariffDraft,
  createTariffRecordsFromDraft,
  getTariffs,
  normalizeTariff,
  saveTariffs,
  validateTariff,
} from "../utils/tariffImportUtils";
import TariffDropzone from "./TariffDropzone";
import TariffMappingEditor from "./TariffMappingEditor";
import TariffPreviewTable from "./TariffPreviewTable";
import TariffValidationResult from "./TariffValidationResult";
import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";

export default function TariffImportPage({ user }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [importError, setImportError] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [tariffs, setTariffs] = useState(() => getTariffs().map(normalizeTariff));
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();

  const validation = useMemo(() => (draft ? validateTariff(draft) : null), [draft]);
  const importedCount = tariffs.length;

  const handleFileSelected = async (selectedFile) => {
    setFile(selectedFile);
    setImportError("");
    setIsAnalyzing(true);

    try {
      const analyzed = await analyzeTariffFile(selectedFile);
      setDraft(normalizeTariff({
        ...analyzed.draft,
        originalFileName: selectedFile.name,
      }));
      setPreview(analyzed.preview);
    } catch (error) {
      setImportError(error.message);
      setDraft(createEmptyTariffDraft());
      setPreview(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveImport = () => {
    if (!draft || !validation?.ok) {
      return;
    }

    const records = createTariffRecordsFromDraft(draft, {
      fileName: file?.name || draft.originalFileName || "",
      user,
      preview,
      validation,
    });
    const nextTariffs = [...tariffs, ...records];

    setTariffs(nextTariffs);
    saveTariffs(nextTariffs);
    triggerSuccessFeedback();
  };

  return (
    <div className="page-stack tariff-import-page">
      <SuccessToast visible={successVisible} />

      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Tarifimport</h1>
        <p className="section-text">
          Tarife per Datei importieren, pruefen und als neue Tarifdatensaetze speichern.
        </p>
      </div>

      <div className="tariff-import-steps">
        <span>1 Upload</span>
        <span>2 Analyse</span>
        <span>3 Vorschau</span>
        <span>4 Mapping</span>
        <span>5 Pruefung</span>
        <span>6 Speichern</span>
        <span>7 Verwaltung</span>
      </div>

      <div className="tariff-import-layout">
        <TariffDropzone file={file} isAnalyzing={isAnalyzing} onFileSelected={handleFileSelected} />

        <section className="card tariff-import-card">
          <div className="section-head">
            <div>
              <h2>Import-Historie</h2>
              <p className="section-text">Gespeicherte Tarife werden in der Tarifverwaltung aktiviert.</p>
            </div>
          </div>
          <div className="tariff-active-summary">
            <strong>{importedCount}</strong>
            <span>gespeicherte Tarifdatensaetze</span>
            <span>Neue Importe ueberschreiben nichts</span>
          </div>
        </section>
      </div>

      {importError && <div className="message message-error">{importError}</div>}

      <TariffPreviewTable preview={preview} />

      <div className="button-row">
        <button type="button" className="button button-dark" onClick={() => setDraft(createEmptyTariffDraft())}>
          Leeren Tarif manuell anlegen
        </button>
      </div>

      <TariffMappingEditor draft={draft} onChange={setDraft} />
      <TariffValidationResult validation={validation} />

      <section className="card tariff-import-card">
        <div className="section-head">
          <div>
            <h2>6. Als neuen Tarif speichern</h2>
            <p className="section-text">
              Der Import wird gespeichert, bleibt zunaechst geprueft und wird erst in der Tarifverwaltung aktiv gesetzt.
            </p>
          </div>
          <div className="entry-summary">
            <span>{user?.username || "Admin"}</span>
            <span>{draft?.tariffName || "Kein Entwurf"}</span>
          </div>
        </div>

        <div className="button-row">
          <button
            type="button"
            className="button button-primary"
            disabled={!validation?.ok}
            onClick={handleSaveImport}
          >
            Als neuen Tarif speichern
          </button>
        </div>
      </section>
    </div>
  );
}
